import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import Modal from 'components/ui/Modal';
import * as TeamsModule from 'modules/teams';

interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** Name and an optional description; groups carry no colour in the UI any more (the API field is optional). */
export const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createTeam = TeamsModule.Hooks.useCreate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      await createTeam.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      setName('');
      setDescription('');
      onSuccess();
    } catch {
      // Error handled by hook
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={t('groups_create')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="team-name">{t('group_settings_name')}</Label>
          <Input
            id="team-name"
            placeholder={t('groups_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="team-description">{t('teams.description_optional')}</Label>
          <Textarea
            id="team-description"
            placeholder={t('groups_description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={!name.trim() || createTeam.isPending}>
            {createTeam.isPending ? t('teams.creating') : t('groups_create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTeamModal;
